import { Logger } from "neo-common";
import {
    IRotationTweenOptions,
    IAlphaTweenOptions,
    IScaleTweenOptions,
    IPositionTweenOptions,
    ISkewTweenOptions,
    IColorTweenOptions,
    ITweenable,
    ITweenOptions,
} from "./Tweens";
import { TimelineLite, TweenLite, Bounce } from "gsap";
import { CustomEase } from "./CustomEase";

/**
 *  Example:
 *
 *  GsapEffects.alphaTween(object, {alphas: alphaTween, tweenEase: ease, tweenDuration: tweenDuration, tweenDelay: tweenDelay});
 *  GsapEffects.rotationTween(object, {degrees: rotateTweenDegrees, tweenEase: ease, tweenDuration: tweenDuration, tweenDelay: tweenDelay});
 *  GsapEffects.scaleTween(object, {tweenX: scaleTweenX, tweenY: scaleTweenY, tweenEase: ease, tweenDuration: tweenDuration});
 *
 */
export class GsapEffects {
    static UseFrames: boolean = false;
    static TickTime: number = 0.0167;

    /**
     * Applies alpha tween on object.
     *
     * @param object
     * @param options
     */
    static alphaTween_TimelineLite(object: ITweenable, options: IAlphaTweenOptions): TimelineLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.alphas.length) * GsapEffects.TickTime;
        const tweenPaused: boolean = options.paused;
        const tweenEaseArray: Array<string> = (options.tweenEase || "Linear.easeNone")
            .split(";")
            .map((value: string) => GsapEffects.getEaseing(value));

        const alphaTimeline: TimelineLite = new TimelineLite({
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay != null && options.tweenDelay > 0 ? options.tweenDelay * GsapEffects.TickTime : 0,
            paused: tweenPaused || false,
        });

        options.alphas.forEach((alpha: number, i: number) => {
            alphaTimeline.to(
                object,
                options.keyTimes && options.keyTimes[i] != null
                    ? (i > 0 ? options.keyTimes[i] - options.keyTimes[i - 1] : options.keyTimes[i]) *
                          tweenDuration *
                          GsapEffects.TickTime
                    : tweenDurationPerPart,
                {
                    alpha: alpha,
                    ease: tweenEaseArray[i] || tweenEaseArray[0],
                    paused: tweenPaused || false,
                },
            );
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            alphaTimeline.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            alphaTimeline.eventCallback("onComplete", alphaTimeline.restart);
        }

        return alphaTimeline;
    }

    /**
     *
     * @param object
     * @param options
     * @returns {TweenLite}
     */
    static alphaTween_Tweenlite(object: ITweenable, options: IAlphaTweenOptions): TweenLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.alphas.length) * GsapEffects.TickTime;
        const tweenEase: string = GsapEffects.getEaseing(options.tweenEase) || "Linear.easeNone";
        const tweenPaused: boolean = options.paused;

        let alphaTweenLite: TweenLite;

        alphaTweenLite = TweenLite.to(object, tweenDurationPerPart, {
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay != null && options.tweenDelay > 0 ? options.tweenDelay * GsapEffects.TickTime : 0,
            alpha: options.alphas,
            ease: tweenEase,
            paused: tweenPaused || false,
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            alphaTweenLite.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            alphaTweenLite.eventCallback("onComplete", alphaTweenLite.restart);
        }

        return alphaTweenLite;
    }

    /**
     * Applies position tween on object, GSAP version.
     * Number of position points X/Y is variable. Count of X's and Y's must be equal.
     *
     * @param object
     * @param options
     */
    static positionTween_TimelineLite(object: ITweenable, options: IPositionTweenOptions): TimelineLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.tweenX.length) * GsapEffects.TickTime;
        const tweenPaused: boolean = options.paused;
        const tweenEaseArray: Array<string> = (options.tweenEase || "Linear.easeNone")
            .split(";")
            .map((value: string) => GsapEffects.getEaseing(value));

        const positionTimeline: TimelineLite = new TimelineLite({
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay != null && options.tweenDelay > 0 ? options.tweenDelay * GsapEffects.TickTime : 0,
            //autoRemoveChildren: true,
        });

        options.tweenX.forEach((tweenX: number, i: number) => {
            positionTimeline.to(
                object.position,
                options.keyTimes && options.keyTimes[i] != null
                    ? (i > 0 ? options.keyTimes[i] - options.keyTimes[i - 1] : options.keyTimes[i]) *
                          tweenDuration *
                          GsapEffects.TickTime
                    : tweenDurationPerPart,
                {
                    useFrames: GsapEffects.UseFrames,
                    x: tweenX,
                    y: options.tweenY[i],
                    ease: tweenEaseArray[i] || tweenEaseArray[0],
                    paused: tweenPaused || false,
                },
            );
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            positionTimeline.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            positionTimeline.eventCallback("onComplete", positionTimeline.restart);
        }

        return positionTimeline;
    }

    static skewTween_TimelineLite(object: ITweenable, options: ISkewTweenOptions): TimelineLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.tweenX.length) * GsapEffects.TickTime;
        const tweenPaused: boolean = options.paused;
        const tweenEaseArray: Array<string> = (options.tweenEase || "Linear.easeNone")
            .split(";")
            .map((value: string) => GsapEffects.getEaseing(value));

        const skewTimeline: TimelineLite = new TimelineLite({
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay != null && options.tweenDelay > 0 ? options.tweenDelay * GsapEffects.TickTime : 0,
            //autoRemoveChildren: true,
        });

        options.tweenX.forEach((tweenX: number, i: number) => {
            skewTimeline.to(
                object.skew,
                options.keyTimes && options.keyTimes[i] != null
                    ? (i > 0 ? options.keyTimes[i] - options.keyTimes[i - 1] : options.keyTimes[i]) *
                          tweenDuration *
                          GsapEffects.TickTime
                    : tweenDurationPerPart,
                {
                    useFrames: GsapEffects.UseFrames,
                    x: tweenX,
                    y: options.tweenY[i],
                    ease: tweenEaseArray[i] || tweenEaseArray[0],
                    paused: tweenPaused || false,
                },
            );
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            skewTimeline.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            skewTimeline.eventCallback("onComplete", skewTimeline.restart);
        }

        return skewTimeline;
    }

    /**
     *
     * @param object
     * @param options
     * @returns {TweenLite}
     */
    static positionTween_Tweenlite(object: ITweenable, options: IPositionTweenOptions): TweenLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.tweenX.length) * GsapEffects.TickTime;
        const tweenEase: string = GsapEffects.getEaseing(options.tweenEase) || "Linear.easeNone";
        const tweenBezier: Object = options.bezier;
        const tweenPaused: boolean = options.paused;

        let positionTweenLite: TweenLite;

        if (tweenBezier != null) {
            positionTweenLite = TweenLite.to(object, tweenDurationPerPart, {
                bezier: tweenBezier || 0,
                useFrames: GsapEffects.UseFrames,
                delay:
                    options.tweenDelay != null && options.tweenDelay > 0
                        ? options.tweenDelay * GsapEffects.TickTime
                        : 0,
                ease: tweenEase,
                paused: tweenPaused || false,
            });
        } else {
            positionTweenLite = TweenLite.to(object.position, tweenDurationPerPart, {
                useFrames: GsapEffects.UseFrames,
                delay:
                    options.tweenDelay != null && options.tweenDelay > 0
                        ? options.tweenDelay * GsapEffects.TickTime
                        : 0,
                x: options.tweenX[0],
                y: options.tweenY[0],
                ease: tweenEase,
                paused: tweenPaused || false,
            });
        }

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            positionTweenLite.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            positionTweenLite.eventCallback("onComplete", positionTweenLite.restart);
        }

        return positionTweenLite;
    }

    static skewTween_Tweenlite(object: ITweenable, options: ISkewTweenOptions): TweenLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.tweenX.length) * GsapEffects.TickTime;
        const tweenEase: string = GsapEffects.getEaseing(options.tweenEase) || "Linear.easeNone";
        const tweenBezier: Object = options.bezier;
        const tweenPaused: boolean = options.paused;

        let skewTweenLite: TweenLite;

        if (tweenBezier != null) {
            skewTweenLite = TweenLite.to(object.skew, tweenDurationPerPart, {
                bezier: tweenBezier || 0,
                useFrames: GsapEffects.UseFrames,
                delay:
                    options.tweenDelay != null && options.tweenDelay > 0
                        ? options.tweenDelay * GsapEffects.TickTime
                        : 0,
                ease: tweenEase,
                paused: tweenPaused || false,
                roundProps: "bezier",
            });
        } else {
            skewTweenLite = TweenLite.to(object.skew, tweenDurationPerPart, {
                useFrames: GsapEffects.UseFrames,
                delay:
                    options.tweenDelay != null && options.tweenDelay > 0
                        ? options.tweenDelay * GsapEffects.TickTime
                        : 0,
                x: options.tweenX[0],
                y: options.tweenY[0],
                ease: tweenEase,
                paused: tweenPaused || false,
            });
        }

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            skewTweenLite.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            skewTweenLite.eventCallback("onComplete", skewTweenLite.restart);
        }

        return skewTweenLite;
    }

    /**
     * Applies rotation tween on object.
     * Number of degrees is variable.
     *
     * @param object
     * @param options
     */
    static rotationTween_TimelineLite(object: ITweenable, options: IRotationTweenOptions): TimelineLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.degrees.length) * GsapEffects.TickTime;
        const tweenPaused: boolean = options.paused;
        const tweenEaseArray: Array<string> = (options.tweenEase || "Linear.easeNone")
            .split(";")
            .map((value: string) => GsapEffects.getEaseing(value));

        const rotationTimeline: TimelineLite = new TimelineLite({
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay != null && options.tweenDelay > 0 ? options.tweenDelay * GsapEffects.TickTime : 0,
            paused: tweenPaused || false,
            //autoRemoveChildren: true,
        });

        options.degrees.map(GsapEffects.degrees2radians).forEach((degree: number, i: number) => {
            rotationTimeline.to(
                object,
                options.keyTimes && options.keyTimes[i] != null
                    ? (i > 0 ? options.keyTimes[i] - options.keyTimes[i - 1] : options.keyTimes[i]) *
                          tweenDuration *
                          GsapEffects.TickTime
                    : tweenDurationPerPart,
                {
                    useFrames: GsapEffects.UseFrames,
                    rotation: degree,
                    ease: tweenEaseArray[i] || tweenEaseArray[0],
                    paused: tweenPaused || false,
                },
            );
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            rotationTimeline.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            rotationTimeline.eventCallback("onComplete", rotationTimeline.restart);

            //restarts, including any delay, and doesn't suppress events during the initial move back to time:0
            //positionTweenLite.restart(true, false);
        }

        return rotationTimeline;
    }

    /**
     *
     * @param object
     * @param options
     * @returns {TweenLite}
     */
    static rotationTween_Tweenlite(object: ITweenable, options: IRotationTweenOptions): TweenLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.degrees.length) * GsapEffects.TickTime;
        const tweenEase: string = GsapEffects.getEaseing(options.tweenEase) || "Linear.easeNone";
        const tweenPaused: boolean = options.paused;

        let rotationTweenLite: TweenLite;

        rotationTweenLite = TweenLite.to(object, tweenDurationPerPart, {
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay != null && options.tweenDelay > 0 ? options.tweenDelay * GsapEffects.TickTime : 0,
            rotation: GsapEffects.degrees2radians(options.degrees[0]),
            ease: tweenEase,
            paused: tweenPaused || false,
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            rotationTweenLite.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            rotationTweenLite.eventCallback("onComplete", rotationTweenLite.restart);
        }

        return rotationTweenLite;
    }

    /**
     * Applies scale tween on object, GSAP version.
     * Number of scale points X/Y is variable. Count of X's and Y's must be equal.
     *
     * @param object
     * @param options
     * @returns {TimelineLite}
     */
    static scaleTween_TimelineLite(object: ITweenable, options: IScaleTweenOptions): TimelineLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.tweenValues.length) * GsapEffects.TickTime;
        const tweenPaused: boolean = options.paused;
        const tweenEaseArray: Array<string> = (options.tweenEase || "Linear.easeNone")
            .split(";")
            .map((value: string) => GsapEffects.getEaseing(value));

        const scaleTimeline: TimelineLite = new TimelineLite({
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay != null && options.tweenDelay > 0 ? options.tweenDelay * GsapEffects.TickTime : 0,
            paused: tweenPaused || false,
        });

        options.tweenValues.forEach((tweenValue: PIXI.Point, i: number) => {
            scaleTimeline.to(
                object.scale,
                options.keyTimes && options.keyTimes[i] != null
                    ? (i > 0 ? options.keyTimes[i] - options.keyTimes[i - 1] : options.keyTimes[i]) *
                          tweenDuration *
                          GsapEffects.TickTime
                    : tweenDurationPerPart,
                {
                    useFrames: GsapEffects.UseFrames,
                    x: tweenValue.x,
                    y: tweenValue.y,
                    ease: tweenEaseArray[i] || tweenEaseArray[0],
                    paused: tweenPaused || false,
                    roundProps: "bezier",
                },
            );
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            scaleTimeline.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            scaleTimeline.eventCallback("onComplete", scaleTimeline.restart);
        }

        return scaleTimeline;
    }

    /**
     *
     * @param object
     * @param options
     * @returns {TweenLite}
     */
    static scaleTween_Tweenlite(object: ITweenable, options: IScaleTweenOptions): TweenLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.tweenValues.length) * GsapEffects.TickTime;
        const tweenEase: string = GsapEffects.getEaseing(options.tweenEase) || "Linear.easeNone";
        const tweenPaused: boolean = options.paused;

        let scaleTweenLite: TweenLite;

        scaleTweenLite = TweenLite.to(object.scale, tweenDurationPerPart, {
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay != null && options.tweenDelay > 0 ? options.tweenDelay * GsapEffects.TickTime : 0,
            x: options.tweenValues[0].x,
            y: options.tweenValues[0].y,
            ease: tweenEase,
            paused: tweenPaused || false,
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            scaleTweenLite.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            scaleTweenLite.eventCallback("onComplete", scaleTweenLite.restart);
        }

        return scaleTweenLite;
    }

    /**
     * Applies tween on object, GSAP version.
     * @param object
     * @param options
     */
    static colorTween_TimelineLite(object: ITweenable, options: IColorTweenOptions): TimelineLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.tweenValues.length) * GsapEffects.TickTime;
        const tweenPaused: boolean = options.paused;
        const tweenEaseArray: Array<string> = (options.tweenEase || "Linear.easeNone")
            .split(";")
            .map((value: string) => GsapEffects.getEaseing(value));

        const timeline: TimelineLite = new TimelineLite({
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay != null && options.tweenDelay > 0 ? options.tweenDelay * GsapEffects.TickTime : 0,
            paused: tweenPaused || false,
        });

        options.tweenValues.forEach((value: number, i: number) => {
            timeline.to(
                object,
                options.keyTimes && options.keyTimes[i] != null
                    ? (i > 0 ? options.keyTimes[i] - options.keyTimes[i - 1] : options.keyTimes[i]) *
                          tweenDuration *
                          GsapEffects.TickTime
                    : tweenDurationPerPart,
                {
                    useFrames: GsapEffects.UseFrames,
                    ease: tweenEaseArray[i] || tweenEaseArray[0],
                    paused: tweenPaused || false,
                    delay: options.tweenDelay[i],
                    colorProps: {
                        [options.tween]: value,
                        format: typeof value == "number" ? "number" : undefined,
                    },
                },
            );
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            timeline.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            timeline.eventCallback("onComplete", timeline.restart);
        }

        return timeline;
    }

    /**
     * Applies tween on object, GSAP version.
     * @param object
     * @param options
     */
    static colorTween_Tweenlite(object: ITweenable, options: IColorTweenOptions): TweenLite {
        const tweenDuration: number =
            options.tweenDuration == null || isNaN(options.tweenDuration)
                ? Math.floor(Math.random() * 120 + 120)
                : options.tweenDuration;
        const tweenDurationPerPart: number = (tweenDuration / options.tweenValues.length) * GsapEffects.TickTime;
        const tweenPaused: boolean = options.paused;

        let tweenlite: TweenLite;

        tweenlite = TweenLite.to(object, tweenDurationPerPart, {
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay || 0,
            ease: GsapEffects.getEaseing(options.tweenEase) || "Linear.easeNone",
            paused: tweenPaused || false,
            colorProps: {
                [options.tween]: options.tweenValues[0],
                format: typeof options.tweenValues[0] == "number" ? "number" : undefined,
            },
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            tweenlite.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            tweenlite.eventCallback("onComplete", tweenlite.restart);

            //restarts, including any delay, and doesn't suppress events during the initial move back to time:0
            //positionTweenLite.restart(true, false);
        }

        return tweenlite;
    }

    /**
     * Applies tween on object, GSAP version.
     * @param object
     * @param options
     */
    static tween_TimelineLite(object: ITweenable, options: ITweenOptions): TimelineLite {
        const tween: Array<string> = options.tween.split(".");
        const tweenProperty: string = tween[tween.length - 1];
        const tweenPaused: boolean = options.paused;
        const timeline: TimelineLite = new TimelineLite({
            useFrames: GsapEffects.UseFrames,
            // delay: (options.tweenDelay != null && options.tweenDelay > 0) ? options.tweenDelay * GsapEffects.TickTime : 0,
            // autoRemoveChildren: true,
        });

        options.tweenValues.forEach((value: number, i: number) => {
            timeline.to(tween.length == 1 ? object : object[tween[0]], options.tweenDuration[i], {
                useFrames: GsapEffects.UseFrames,
                [tweenProperty]: value,
                ease: GsapEffects.getEaseing(options.tweenEase[i]) || "Linear.easeNone",
                paused: tweenPaused || false,
                delay: options.tweenDelay[i],
            });
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            timeline.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.callbacksInTime != null) {
            options.callbacksInTime.forEach((callback: (params: string) => void, duration: number) => {
                timeline.add(callback, duration * GsapEffects.TickTime);
            });
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            timeline.eventCallback("onComplete", timeline.restart);

            //restarts, including any delay, and doesn't suppress events during the initial move back to time:0
            //positionTweenLite.restart(true, false);
        }

        return timeline;
    }

    /**
     * Applies tween on object, GSAP version.
     * @param object
     * @param options
     */
    static tween_Tweenlite(object: ITweenable, options: ITweenOptions): TweenLite {
        const tween: Array<string> = options.tween.split(".");
        const tweenProperty: string = tween[tween.length - 1];
        const tweenPaused: boolean = options.paused;

        let tweenlite: TweenLite;

        tweenlite = TweenLite.to(tween.length == 1 ? object : object[tween[0]], options.tweenDuration[0], {
            useFrames: GsapEffects.UseFrames,
            delay: options.tweenDelay[0],
            [tweenProperty]: options.tweenValues[0],
            ease: GsapEffects.getEaseing(options.tweenEase[0]) || "Linear.easeNone",
            paused: tweenPaused || false,
        });

        if (options.callbackFunction != null) {
            if (options.callbackType == null) {
                options.callbackType = "onComplete";
            }
            tweenlite.eventCallback(
                options.callbackType,
                options.callbackFunction,
                options.callbackParams,
                options.callbackScope,
            );
        }

        if (options.tweenLoop) {
            //restarts, not including any delay that was defined
            tweenlite.eventCallback("onComplete", tweenlite.restart);

            //restarts, including any delay, and doesn't suppress events during the initial move back to time:0
            //positionTweenLite.restart(true, false);
        }

        return tweenlite;
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    static degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     * Return easing function for tweening.
     * @method getEaseing
     * @param ease string - name of easeing
     * @return any
     */
    static getEaseing(ease: string): any {
        switch ("" + ease) {
            case "0":
            case "linear":
                return "Linear.easeNone";
            case "1":
            case "backIn":
                return "Back.easeIn";
            case "2":
            case "backInOut":
                return "Back.easeInOut";
            case "3":
            case "backOut":
                return "Back.easeOut";
            case "4":
            case "bounceIn":
                return "Bounce.easeIn";
            case "5":
            case "bounceInOut":
                return "Bounce.easeInOut";
            case "6":
            case "bounceOut":
                return "Bounce.easeOut";
            case "7":
            case "circIn":
                return "Circ.easeIn";
            case "8":
            case "circInOut":
                return "Circ.easeInOut";
            case "9":
            case "circOut":
                return "Circ.easeOut";
            case "10":
            case "cubicIn":
                return "Cubic.easeIn";
            case "11":
            case "cubicInOut":
                return "Cubic.easeInOut";
            case "12":
            case "cubicOut":
                return "Cubic.easeOut";
            case "13":
            case "elasticIn":
                return "Elastic.easeIn";
            case "14":
            case "elasticInOut":
                return "Elastic.easeInOut";
            case "15":
            case "elasticOut":
                return "Elastic.easeOut";
            case "16":
            case "quadIn":
                return "Quad.easeIn";
            case "17":
            case "quadInOut":
                return "Quad.easeInOut";
            case "18":
            case "quadOut":
                return "Quad.easeOut";
            case "19":
            case "quartIn":
                return "Quart.easeIn";
            case "20":
            case "quartInOut":
                return "Quart.easeInOut";
            case "21":
            case "quartOut":
                return "Quart.easeOut";
            case "22":
            case "quintIn":
                return "Quint.easeIn";
            case "23":
            case "quintInOut":
                return "Quint.easeInOut";
            case "24":
            case "quintOut":
                return "Quint.easeOut";
            case "25":
            case "sineIn":
                return "Sine.easeIn";
            case "26":
            case "sineInOut":
                return "Sine.easeInOut";
            case "27":
            case "sineOut":
                return "Sine.easeOut";
            case "28":
            case "expoOut":
                return "Expo.easeOut";
            case "29":
            case "expoIn":
                return "Expo.easeIn";
            case "power1In":
                return "Power1.easeIn";
            case "power2In":
                return "Power2.easeIn";
            case "power2InOut":
                return "Power2.easeInOut";
            case "power3In":
                return "Power3.easeIn";
            case "power3InOut":
                return "Power3.easeInOut";
            case "power4In":
                return "Power4.easeIn";
            default:
                return CustomEase.get(ease) != null ? ease : "Linear.easeNone";
        }
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected static log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__ || "GsapEffects";
            Logger.debugGroup(compName, s);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Effects/GsapEffects.ts
 **/